import { z } from 'zod';
import { CountryCode, CurrencyCode, IsoDateTime, Money, MoneyAmount, OrgSlug } from './primitives.js';
/**
 * Domain Zod schemas for JSONB fields and cross-boundary payloads.
 * These are the contracts that outlive individual PRs — breaking changes
 * require a migration.
 */
// ---------------------------------------------------------------------------
// PayComponent metadata
// ---------------------------------------------------------------------------
export const PayComponentKind = z.enum([
    'regular',
    'allowance',
    'thirteenth_month',
    'bonus',
    'severance',
    'final_settlement',
    // System-computed accrued-leave encashment on termination (ADR-0008
    // D35, PR ④m). Written directly by the terminate handler, never via
    // the operator pay-component route — so it is absent from
    // CreatePayComponentInput's narrower kind enum by design.
    'termination_payout',
    // Employee-advance repayment line (ADR-0009, PR ⑦a). System-emitted by the
    // post-calculator advance-deduction pass, never via the operator route.
    'loan_deduction',
    // Termination-time advance recovery (ADR-0009 D10, PR ⑦c). System-written by
    // the terminate handler as a negative component that nets an outstanding
    // advance balance against the final settlement; never via the operator route.
    'loan_clawback',
    // Overtime earning (ADR-0014 Phase 3, D-1). System-emitted by the attendance
    // layer's overtime-approval transaction (NEVER the operator route): the
    // approved OvertimeItem mints this component with its precomputed amount. A
    // taxable gross addition (nature `other_taxable`) routed through the same
    // allowance path as bonus/13th-month — see SUPPORTED_KINDS in
    // buildPayrollSnapshot. Exempt from the unpaid-leave factor in the
    // calculator (it is pay for hours actually worked).
    'overtime',
    // Expense reimbursement (ADR-0020 Phase 3, D-3). System-minted by the payroll
    // SWEEP at run-create (never the operator route): an approved ExpenseClaim due
    // on/before the run's payDate mints this component, pinned effectiveFrom =
    // effectiveTo = payDate so the frozen snapshot pays it once via the #226
    // non-taxable rail. nature = the claim's category → `other_nontaxable` in v1.
    // Exempt from the unpaid-leave factor (a reimbursement is not salary).
    'reimbursement',
    'deduction',
]);
/**
 * Overtime approval lifecycle (ADR-0014 D-4 v0.3, two-stage). Three-site
 * discipline (Prisma + Zod + inferred TS). Owned by the `OvertimeItem` model:
 *
 *   computed → pending_manager → pending_hr_admin → approved | rejected
 *
 * Stage 1 = line manager (`pending_manager`), Stage 2 = HR Admin = admin/owner
 * (`pending_hr_admin`). The overtime PayComponent is minted ONLY on the final
 * `approved` transition (Stage 2) — so pending/rejected overtime has no
 * PayComponent and structurally CANNOT enter a payroll run (the run-filter
 * invariant is a structural impossibility, not a conditional filter).
 */
export const OvertimeStatus = z.enum([
    'computed',
    'pending_manager',
    'pending_hr_admin',
    'approved',
    'rejected',
]);
// ---------------------------------------------------------------------------
// Performance Reviews (ADR-0013 Phase 1). Three-site enums + the two JSONB
// payload schemas (template definition + response ratings). The sub-objects are
// `.strict()` — the Phase-3 lesson: a non-strict parent silently strips unknown
// keys, which must NOT happen on operator-authored template/response payloads
// (a typo must be a loud rejection, D-6).
// ---------------------------------------------------------------------------
export const PerformanceCycleStatus = z.enum(['draft', 'goal_setting', 'active', 'review', 'closed']);
export const PerformanceGoalStatus = z.enum(['draft', 'agreed']);
export const PerformanceGoalOwnerType = z.enum(['organization', 'employee']);
export const ReviewAssignmentType = z.enum(['self', 'manager', 'peer']);
/** One competency dimension ("how you work"), rated on the template scale. */
export const ReviewCompetency = z
    .object({
    key: z.string().min(1),
    label: z.string().min(1),
    description: z.string().optional(),
})
    .strict();
/** Configurable rating scale (D-13). */
export const ReviewRatingScale = z
    .object({
    min: z.number(),
    max: z.number(),
    /** Optional value→label map, e.g. {"1":"Below","5":"Exceptional"}. */
    labels: z.record(z.string(), z.string()).optional(),
})
    .strict()
    .refine((s) => s.max > s.min, { message: 'max must exceed min' });
/** How the weighted-KRA score and competency score combine (D-2b). */
export const ReviewCombinationRule = z
    .object({
    kraWeight: z.number().min(0).max(1),
    competencyWeight: z.number().min(0).max(1),
})
    .strict()
    .refine((r) => Math.abs(r.kraWeight + r.competencyWeight - 1) < 1e-9, {
    message: 'kraWeight + competencyWeight must sum to 1',
});
/** ReviewTemplate.definition — strict JSONB. */
export const ReviewTemplateDefinition = z
    .object({
    competencies: z.array(ReviewCompetency),
    ratingScale: ReviewRatingScale,
    combinationRule: ReviewCombinationRule,
})
    .strict();
/** One rated item — a KRA (ref=goalId) or a competency (ref=competency key). */
export const ReviewItemRating = z
    .object({
    ref: z.string().min(1),
    score: z.number(),
    comment: z.string().max(2000).optional(),
})
    .strict();
/** ReviewResponse.ratings — strict JSONB. */
export const ReviewResponseRatings = z
    .object({
    kraRatings: z.array(ReviewItemRating),
    competencyRatings: z.array(ReviewItemRating),
    overall: z.number().optional(),
    comments: z.string().max(5000).optional(),
})
    .strict();
export const PayComponentMetadata = z.object({
    kind: PayComponentKind,
    taxable: z.boolean(),
    notes: z.string().optional(),
    /**
     * `sourceReference` is used by severance/final_settlement to link to the
     * termination event that created the component. Empty for other kinds.
     */
    sourceReference: z.string().optional(),
});
// ---------------------------------------------------------------------------
// ComplianceConfig.registrations
// ---------------------------------------------------------------------------
export const RegistrationRecord = z.object({
    authority: z.string(),
    registrationNumber: z.string(),
    verified: z.boolean(),
    verifiedAt: IsoDateTime.optional(),
});
export const RegistrationsByCountry = z.record(CountryCode, z.array(RegistrationRecord));
// ---------------------------------------------------------------------------
// PayrollRun.totals
// ---------------------------------------------------------------------------
export const PayrollTotals = z.object({
    grossTotal: Money,
    taxTotal: Money,
    socialSecurityTotal: Money,
    netTotal: Money,
    employerContributionsTotal: Money,
    employeeCount: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Payslip.breakdown
// ---------------------------------------------------------------------------
export const PayslipLineItem = z.object({
    kind: z.enum(['earning', 'deduction', 'employer_contribution', 'info']),
    label: z.string(),
    amount: Money,
    taxable: z.boolean().optional(),
    ruleReference: z.string().optional(),
});
export const PayslipBreakdown = z.object({
    earnings: z.array(PayslipLineItem),
    deductions: z.array(PayslipLineItem),
    employerContributions: z.array(PayslipLineItem),
    net: Money,
    gross: Money,
    currency: CurrencyCode,
});
// ---------------------------------------------------------------------------
// PayrollRun.calculationInputsSnapshot
// ---------------------------------------------------------------------------
/**
 * Frozen at PayrollRun creation. Contains everything the calculator needs to
 * replay the run byte-identically three years from now. Paired with
 * `ruleSpecHash` and `interpreterVersion` for tamper evidence.
 */
export const CalculationInputsSnapshot = z.object({
    /** ISO timestamp when the snapshot was taken. */
    snapshotAt: IsoDateTime,
    /** Organization context at time of snapshot. */
    organization: z.object({
        id: z.string(),
        countryCode: CountryCode,
        baseCurrency: CurrencyCode,
        stateOfRecord: z.string().nullable(),
    }),
    /**
     * The full rule spec(s) serialized. Keyed by country so multi-country orgs
     * can resolve per-employee. These are the canonical YAML specs parsed into
     * JSON; re-hashing them must produce `ruleSpecHash`.
     */
    rules: z.record(CountryCode, z.unknown()),
    /**
     * FX rates active on payDate for every distinct currency pair in the run.
     * Rates are strings to preserve precision.
     */
    exchangeRates: z.array(z.object({
        fromCurrency: CurrencyCode,
        toCurrency: CurrencyCode,
        rate: z.string().regex(/^\d+(\.\d{1,8})?$/),
        effectiveDate: z.string(), // YYYY-MM-DD
        source: z.string(),
    })),
    /**
     * Public holidays in the period for every country in the run. Includes
     * state-level holidays where stateCode is set.
     */
    holidays: z.array(z.object({
        countryCode: CountryCode,
        stateCode: z.string().nullable(),
        date: z.string(), // YYYY-MM-DD
        name: z.string(),
        kind: z.enum(['public', 'religious', 'regional']),
    })),
    /** Minimum wage per country at time of snapshot. */
    minimumWages: z.array(z.object({
        countryCode: CountryCode,
        monthly: MoneyAmount,
        currency: CurrencyCode,
    })),
    /**
     * Employee snapshot — what each employee looked like at time of run.
     * Deleting or editing an employee after the run never changes history.
     */
    employees: z.array(z.object({
        id: z.string(),
        employeeNumber: z.string(),
        countryCode: CountryCode,
        stateCode: z.string().nullable(),
        hireDate: IsoDateTime,
        terminationDate: IsoDateTime.nullable(),
        compensation: z.object({
            grossSalary: MoneyAmount,
            currency: CurrencyCode,
            payFrequency: z.enum(['monthly', 'bi_weekly', 'weekly']),
        }),
        payComponents: z.array(z.object({
            kind: PayComponentKind,
            label: z.string(),
            amount: MoneyAmount,
            currency: CurrencyCode,
            taxable: z.boolean(),
        })),
    })),
});
// ---------------------------------------------------------------------------
// MigrationJob.driftReport
// ---------------------------------------------------------------------------
/**
 * Produced by `packages/migration-mokoafrika` during dry-run. Lists every
 * discrepancy between source (MokoAfrika) and target (Kora) with a severity.
 * Cutover is blocked while any `error`-severity discrepancy exists.
 */
export const DriftReport = z.object({
    generatedAt: IsoDateTime,
    sourceSystem: z.string(),
    totals: z.object({
        sourceEmployeeCount: z.number().int().nonnegative(),
        targetEmployeeCount: z.number().int().nonnegative(),
        sourcePayslipCount: z.number().int().nonnegative(),
        targetPayslipCount: z.number().int().nonnegative(),
    }),
    discrepancies: z.array(z.object({
        severity: z.enum(['info', 'warning', 'error']),
        entityType: z.string(),
        entityId: z.string(),
        field: z.string(),
        sourceValue: z.unknown(),
        targetValue: z.unknown(),
        delta: z.string().optional(),
        message: z.string(),
    })),
});
// ---------------------------------------------------------------------------
// Employee — shared request/response shapes
// ---------------------------------------------------------------------------
export const EmployeeStatus = z.enum(['active', 'on_leave', 'terminated']);
/** HR celebration occasions (ADR-0015 Phase 2). Three-site: Prisma enum + this
 *  Zod enum + the inferred TS type. */
export const CelebrationOccasion = z.enum(['birthday', 'work_anniversary']);
export const TerminationReason = z.enum([
    'resignation',
    'dismissal',
    'retirement',
    'end_of_contract',
    'death',
]);
/**
 * Self-identified gender. Inclusive enum supporting modern HR practice
 * + legal reporting (gender-pay-gap, EEO filings). All optional —
 * employees may decline to report.
 */
export const Gender = z.enum([
    'male',
    'female',
    'non_binary',
    'other',
    'prefer_not_to_say',
]);
/**
 * How an employee is classified for HR + compliance purposes. Drives
 * benefits eligibility, tax-withholding defaults, and reporting
 * buckets. Mirrors the Prisma enum.
 */
export const EmploymentType = z.enum([
    'full_time',
    'part_time',
    'contract',
    'intern',
]);
/**
 * PayrollModel (ADR 0010) — the statutory regime for an employee.
 * ORTHOGONAL to EmploymentType (work arrangement). `local_employee` is
 * the standard local-payroll default (current behaviour); `international_remote`
 * is a cross-border worker with no statutory deductions. v1 values only —
 * contractor/peo_managed/secondment are deferred per the ADR. Mirrors the
 * Prisma PayrollModel enum. See ADR 0010 §Glossary.
 */
export const PayrollModel = z.enum(['local_employee', 'international_remote']);
/**
 * Commercial subscription enums (Phase C-1, SDD v1.0 §2.1/§2.2). Second site of
 * the three-site discipline — these MUST stay in lockstep with the Prisma enums
 * SubscriptionTier / SubscriptionStatus / PaymentProvider in
 * packages/db/prisma/schema.prisma. Value ORDER is locked and observable
 * (architect 2026-06-07): tier price-ascending, status lifecycle, provider per
 * STRATEGY §6.1 chronology. Naming follows the PayrollModel template (bare name,
 * not a `*Schema` suffix). No SUPPORTED_* allowlist in C-1 (no runtime gate yet).
 */
export const SubscriptionTier = z.enum(['starter', 'growth', 'enterprise']);
export const SubscriptionStatus = z.enum([
    'trial',
    'active',
    'past_due',
    'read_only',
    'suspended',
    'cancelled',
]);
export const PaymentProvider = z.enum(['manual', 'paddle', 'stripe']);
/**
 * Invoice enums + line-item shape (Phase D-2, SDD §2.3). Second site of the
 * three-site discipline — mirrors the Prisma `InvoiceStatus` enum. Bare-name
 * convention (like SubscriptionStatus), lifecycle order. The line-item total is
 * operator-supplied and NOT cross-validated against quantity × unitAmount (Phase
 * 1 flexibility, locked decision 2026-06-08).
 */
export const InvoiceStatus = z.enum(['draft', 'issued', 'paid', 'overdue', 'voided']);
export const InvoiceLineItem = z.object({
    description: z.string().trim().min(1).max(500),
    quantity: z.number().int().positive().default(1),
    unitAmount: z.number().nonnegative(),
    totalAmount: z.number().nonnegative(),
});
/**
 * ChangePayrollModelInput — request body for PATCH /v1/employees/:id/payroll-model
 * (ADR 0010 Phase 2). owner/admin only; `reason` is mandatory (audit-logged).
 * `confirmMisclassificationWarning` acknowledges the soft warning raised when
 * setting `international_remote` on an employee who resides in the tenant's
 * primary registered country (a likely-but-not-always misclassification).
 */
export const ChangePayrollModelInput = z
    .object({
    payrollModel: PayrollModel,
    reason: z.string().trim().min(1, 'A reason is required.').max(500),
    confirmMisclassificationWarning: z.boolean().optional().default(false),
})
    .strict();
/**
 * SWIFT/BIC code per ISO 9362. 8 chars (bank only) or 11 chars
 * (bank + branch). Alphanumeric, all uppercase. We validate the
 * shape because malformed SWIFT codes silently break international
 * wire transfers.
 */
export const BankSwiftCode = z
    .string()
    .regex(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/, 'SWIFT/BIC must be 8 or 11 chars: 6 letters (bank) + 2 alphanumeric (country/location) + optional 3 alphanumeric (branch)');
/**
 * Human-readable employee number, unique per org.
 * Allows alphanumeric + hyphens; 3-32 chars. No spaces (spreadsheet-friendly).
 */
export const EmployeeNumber = z
    .string()
    .min(3)
    .max(32)
    .regex(/^[A-Za-z0-9][A-Za-z0-9-]*$/, 'Employee number must start with alphanumeric and contain only alphanumerics and hyphens');
/**
 * ISO state code within a country (e.g. "LA" for Lagos State in NG). Optional;
 * required for NG payroll filing, irrelevant elsewhere at Sprint 0.
 */
export const StateCode = z.string().regex(/^[A-Z]{2}$/, 'State code must be two uppercase letters');
/**
 * NextOfKin — emergency / dependent contact stored as a JSON column on
 * Employee. We use JSON instead of five sparse columns because nothing
 * else queries this data; HR pulls it for emergencies, payroll never
 * touches it, and the shape may grow (multiple kin, beneficiary
 * percentages) without a migration.
 *
 * Relationship is free-text — the realistic enum is too long
 * (spouse, partner, parent, child, sibling, friend, guardian, ...) and
 * locale-dependent. Cap at 50 to keep DB rows reasonable.
 */
export const NextOfKin = z.object({
    name: z.string().min(1).max(100),
    relationship: z.string().min(1).max(50),
    phoneNumber: z.string().min(4).max(30),
    email: z.string().email().max(255).optional(),
    address: z.string().max(500).optional(),
});
/**
 * CreateEmployeeInput — request body for POST /v1/employees. Shared between
 * the BE (input validation) and FE forms.
 */
export const CreateEmployeeInput = z.object({
    employeeNumber: EmployeeNumber,
    firstName: z.string().min(1).max(100),
    middleName: z.string().min(1).max(100).optional(),
    lastName: z.string().min(1).max(100),
    email: z.string().email().max(255).optional(),
    phone: z.string().min(4).max(30).optional(),
    gender: Gender.optional(),
    countryCode: CountryCode,
    stateCode: StateCode.optional(),
    taxIdentifier: z.string().min(1).max(50).optional(),
    socialSecurityId: z.string().min(1).max(50).optional(),
    hireDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'hireDate must be YYYY-MM-DD'),
    // Tier 1 HRIS fields requested by Mokoafrika to consolidate off
    // Bamboo HR. All optional — operators populate over time.
    dateOfBirth: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'dateOfBirth must be YYYY-MM-DD')
        .optional(),
    homeAddress: z.string().min(1).max(500).optional(),
    nextOfKin: NextOfKin.optional(),
    // Platform HR fields — every serious HR product has these. All
    // optional; operators populate as data is entered. Future PRs may
    // promote department/position/workLocation to FK relations once
    // we build org-chart catalogues.
    department: z.string().min(1).max(100).optional(),
    position: z.string().min(1).max(100).optional(),
    employmentType: EmploymentType.optional(),
    // ADR 0010 Phase 1: accepted at create time, defaults to local_employee
    // server-side when omitted. Orthogonal to employmentType. NOT mutable via
    // PATCH in Phase 1 (Phase 2 adds a dedicated change-model action).
    payrollModel: PayrollModel.optional(),
    workLocation: z.string().min(1).max(100).optional(),
    // Bank details for salary credit. Added at create time (2026-06-09,
    // bank-export feature) to close the create-vs-update asymmetry — previously
    // only settable via PATCH. All optional; the bank-export coverage check (at
    // download time) is what enforces population, not creation.
    bankSwiftCode: BankSwiftCode.optional(),
    bankName: z.string().min(1).max(100).optional(),
    bankAccountNumber: z.string().min(1).max(34).optional(),
    bankAccountName: z.string().min(1).max(150).optional(),
});
/**
 * UpdateEmployeeInput — request body for PATCH /v1/employees/:id. All fields
 * optional; server rejects empty bodies. `employeeNumber` and `hireDate` are
 * NOT updatable — they're load-bearing for history. `countryCode` is editable
 * ONLY for `international_remote` employees (ADR 0010 Phase 2); the handler
 * rejects country changes on `local_employee`, where country drives statutory
 * math and stays append-only.
 */
export const UpdateEmployeeInput = z
    .object({
    firstName: z.string().min(1).max(100),
    middleName: z.string().min(1).max(100).nullable(),
    lastName: z.string().min(1).max(100),
    email: z.string().email().max(255).nullable(),
    phone: z.string().min(4).max(30).nullable(),
    gender: Gender.nullable(),
    // ADR 0010 Phase 2: gated in the handler — only international_remote
    // employees may change country. Non-nullable (an employee always has one).
    countryCode: CountryCode,
    stateCode: StateCode.nullable(),
    taxIdentifier: z.string().min(1).max(50).nullable(),
    socialSecurityId: z.string().min(1).max(50).nullable(),
    // HRIS fields — nullable so operators can clear a value, not just set it.
    dateOfBirth: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'dateOfBirth must be YYYY-MM-DD')
        .nullable(),
    homeAddress: z.string().min(1).max(500).nullable(),
    nextOfKin: NextOfKin.nullable(),
    // Platform HR fields — same nullable pattern; operator can clear.
    department: z.string().min(1).max(100).nullable(),
    position: z.string().min(1).max(100).nullable(),
    employmentType: EmploymentType.nullable(),
    workLocation: z.string().min(1).max(100).nullable(),
    bankSwiftCode: BankSwiftCode.nullable(),
    bankName: z.string().min(1).max(100).nullable(),
    bankAccountNumber: z.string().min(1).max(34).nullable(),
    bankAccountName: z.string().min(1).max(150).nullable(),
    /// Direct manager FK (ADR 0008 D19). cuid format — same shape as
    /// every other Prisma-issued id. null clears the assignment.
    /// The API rejects cycles (A → B → A) and same-org-violations
    /// at write time.
    managerId: z.string().min(1).max(100).nullable(),
    status: z.enum(['active', 'on_leave']), // terminate is a separate endpoint
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, {
    message: 'At least one field must be provided',
});
/**
 * TerminateEmployeeInput — request body for POST /v1/employees/:id/terminate.
 */
export const TerminateEmployeeInput = z.object({
    terminationDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'terminationDate must be YYYY-MM-DD'),
    reason: TerminationReason,
    note: z.string().max(500).optional(),
});
/**
 * ListEmployeesQuery — query string for GET /v1/employees.
 */
export const ListEmployeesQuery = z.object({
    status: EmployeeStatus.optional(),
    countryCode: CountryCode.optional(),
    /** Filter to a single department (exact match, case-sensitive). */
    department: z.string().min(1).max(100).optional(),
    /** Filter by employment type. */
    employmentType: EmploymentType.optional(),
    cursor: z.string().optional(), // opaque pagination cursor
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/**
 * EmployeeResponse — public shape. Excludes timestamps that don't matter for
 * display, exposes what FE forms and list views need.
 */
export const EmployeeResponse = z.object({
    id: z.string(),
    employeeNumber: EmployeeNumber,
    firstName: z.string(),
    middleName: z.string().nullable(),
    lastName: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    gender: Gender.nullable(),
    countryCode: CountryCode,
    stateCode: z.string().nullable(),
    taxIdentifier: z.string().nullable(),
    socialSecurityId: z.string().nullable(),
    // dateOfBirth is YYYY-MM-DD on the wire — not full ISO datetime —
    // because it's a calendar date, not a moment. Time-zone'd birthdays
    // are a famous source of off-by-one bugs.
    dateOfBirth: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    homeAddress: z.string().nullable(),
    nextOfKin: NextOfKin.nullable(),
    department: z.string().nullable(),
    position: z.string().nullable(),
    employmentType: EmploymentType.nullable(),
    // ADR 0010: NOT NULL in the DB (default local_employee), so the response
    // value is always a valid PayrollModel — non-nullable on the wire.
    payrollModel: PayrollModel,
    workLocation: z.string().nullable(),
    bankSwiftCode: z.string().nullable(),
    bankName: z.string().nullable(),
    bankAccountNumber: z.string().nullable(),
    bankAccountName: z.string().nullable(),
    /**
     * Personal email — distinct from work `email`. Employee-managed via
     * the /me self-service portal. Nullable on the wire because operators
     * may not have collected it yet for older employees.
     */
    personalEmail: z.string().email().nullable(),
    /// Direct manager FK (ADR 0008 D19). Null for unmanaged employees
    /// and the org owner.
    managerId: z.string().nullable(),
    /// Resolved manager name — only populated by the GET-by-id admin
    /// endpoint (uses Prisma include). Absent from list responses.
    manager: z.object({ firstName: z.string(), lastName: z.string() }).nullable().optional(),
    /// Linked login account — only populated by the GET-by-id admin endpoint.
    /// `null` when the employee has no login yet. Drives the provision-vs-
    /// change-role action on the employee detail page.
    user: z
        .object({
        id: z.string(),
        email: z.string(),
        // Inlined (not the `UserRole` const) because that's declared further
        // down this file and EmployeeResponse can't forward-reference it.
        role: z.enum(['owner', 'admin', 'hr', 'finance', 'employee', 'it']),
        status: z.enum(['invited', 'active', 'disabled']),
    })
        .nullable()
        .optional(),
    hireDate: IsoDateTime,
    terminationDate: IsoDateTime.nullable(),
    terminationReason: TerminationReason.nullable(),
    status: EmployeeStatus,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
/**
 * TerminationSettlement — the financial outcome computed at termination
 * (ADR-0008 D35 / ADR-0009 D10), surfaced in the terminate response so the UI
 * can show it at the point of decision. `payout` is the accrued-annual-leave
 * encashment (null when there's nothing to encash); `advanceDisposition` is how
 * outstanding advances were settled (null when there were none). These are the
 * same values written to the audit trail — display, not a fresh computation.
 */
export const TerminationSettlement = z.object({
    payout: z
        .object({
        amount: z.string(), // Decimal string, in `currency`
        currency: z.string(),
        unusedAnnualDays: z.number(),
    })
        .nullable(),
    advanceDisposition: z
        .object({
        disposition: z.string(), // forgive | net_against_settlement
        totalClawback: z.string(),
        advanceCount: z.number(),
    })
        .nullable(),
});
/** TerminateEmployeeResult — response body for POST /v1/employees/:id/terminate. */
export const TerminateEmployeeResult = z.object({
    employee: EmployeeResponse,
    settlement: TerminationSettlement,
});
// ---------------------------------------------------------------------------
// Exchange Rates API — request/response schemas
// ---------------------------------------------------------------------------
/**
 * Where the rate came from. Free-text on the DB side for forward-compat,
 * but the UI offers a curated list.
 *
 * **`open_market`** is the recommended source for African currencies.
 * Central-bank rates (CBN, BCC, BOG) are routinely 20-30% off the rate
 * an actual USD-to-NGN swap clears at, and PenCom contributions on a
 * CBN-rate basis can underpay an expat by thousands of dollars a year.
 * Open-market aggregators (Xe, exchangerate-api.com, OpenExchangeRates)
 * track interbank/parallel-market rates that match what employees
 * actually realize when converting.
 *
 * The provider behind `open_market` is swappable without DB migration —
 * source string stays generic; the audit log captures which provider
 * was used in the `note` / `reason` field.
 */
export const ExchangeRateSource = z.enum([
    'manual',
    // Open-market aggregator sync (Xe-style). Provider details captured
    // in the audit log, not the source string.
    'open_market',
    'central_bank_ng',
    'central_bank_gh',
    'central_bank_cd',
    'openexchangerates',
    'oanda',
    'other',
]);
/**
 * FX rate precision. Stored as Decimal(18,8) on the server; here we accept
 * a string with up to 8 fractional digits. Matches MoneyAmount's string-
 * to-avoid-float-drift discipline.
 */
export const FxRateString = z
    .string()
    .regex(/^(?!0+(\.0+)?$)\d+(\.\d{1,8})?$/, 'Rate must be a positive decimal with up to 8 fractional digits (cannot be zero)');
/**
 * CreateExchangeRateInput — request body for POST /v1/exchange-rates.
 *
 * Admin flow: set one direction (e.g. USD→NGN @ 1500). The snapshot
 * builder auto-inverts on read if the reverse pair is queried, so you
 * don't need to enter both directions unless you want asymmetric bid/ask
 * rates (rare).
 *
 * Uniqueness is (fromCurrency, toCurrency, effectiveDate) — posting the
 * same triple twice returns 409 with the existing rate. To update a
 * rate, post a new row with a later effectiveDate; historical payroll
 * runs keep pointing at the rate active on their payDate.
 */
export const CreateExchangeRateInput = z
    .object({
    fromCurrency: CurrencyCode,
    toCurrency: CurrencyCode,
    rate: FxRateString,
    effectiveDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'effectiveDate must be YYYY-MM-DD'),
    source: ExchangeRateSource,
    /**
     * Free-text note — what made you set this rate. Persisted on an
     * audit event alongside the row. Optional for `manual` + API sources;
     * strongly encouraged for overrides from official central-bank rates.
     */
    note: z.string().max(500).optional(),
})
    .refine((v) => v.fromCurrency !== v.toCurrency, {
    message: 'fromCurrency and toCurrency must differ',
    path: ['toCurrency'],
});
/** ListExchangeRatesQuery — query string for GET /v1/exchange-rates. */
export const ListExchangeRatesQuery = z.object({
    fromCurrency: CurrencyCode.optional(),
    toCurrency: CurrencyCode.optional(),
    /** Only return rates effective on or before this date (YYYY-MM-DD). */
    asOf: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    limit: z.coerce.number().int().min(1).max(500).default(200),
});
export const ExchangeRateResponse = z.object({
    id: z.string(),
    fromCurrency: CurrencyCode,
    toCurrency: CurrencyCode,
    rate: FxRateString,
    effectiveDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    source: z.string(), // stored as free-text on the DB
    createdAt: IsoDateTime,
});
export const ExchangeRateListResponse = z.object({
    rates: z.array(ExchangeRateResponse),
});
// ---------------------------------------------------------------------------
// User API — request/response schemas
// ---------------------------------------------------------------------------
/** Mirrors the Prisma UserRole enum. */
export const UserRole = z.enum(['owner', 'admin', 'hr', 'finance', 'employee', 'it']);
/** Mirrors the Prisma UserStatus enum. */
export const UserStatus = z.enum(['invited', 'active', 'disabled']);
/**
 * UserResponse — public shape for GET /v1/users/me.
 *
 * `employeeId` is non-null when the user is linked to an Employee record
 * (for employee self-service in Sprint 2+). For admin/owner/hr/finance
 * users — the typical Sprint 0 set — it's null.
 *
 * Never includes passwordHash, refreshTokens, or any other sensitive
 * material. Changes here are a public-contract change; bump carefully.
 */
export const UserResponse = z.object({
    id: z.string(),
    organizationId: z.string(),
    email: z.string().email(),
    role: UserRole,
    status: UserStatus,
    employeeId: z.string().nullable(),
    lastLoginAt: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
// ---------------------------------------------------------------------------
// Compensation API — request/response schemas
// ---------------------------------------------------------------------------
/** PayFrequency mirrors the Prisma enum. */
export const PayFrequency = z.enum(['monthly', 'bi_weekly', 'weekly']);
/**
 * CreateCompensationInput — request body for POST /v1/compensations.
 *
 * The server auto-closes the previous open compensation (effectiveTo=null)
 * for this employee in the same transaction: it sets that record's
 * effectiveTo to the day BEFORE `effectiveFrom` on the new record. This
 * preserves history without requiring the client to do manual cleanup.
 *
 * `grossSalary` is the employee's basic monthly pay (or bi-weekly/weekly
 * depending on payFrequency). This is the amount the payroll orchestrator
 * uses as `basicMonthly` after converting by frequency. Pay components
 * (housing / transport allowances, bonuses) are separate rows on
 * PayComponent — not handled by this endpoint.
 */
export const CreateCompensationInput = z.object({
    employeeId: z.string().min(1),
    effectiveFrom: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'effectiveFrom must be YYYY-MM-DD'),
    grossSalary: MoneyAmount,
    currency: CurrencyCode,
    payFrequency: PayFrequency,
});
/** Query string for GET /v1/compensations. */
export const ListCompensationsQuery = z.object({
    employeeId: z.string().min(1),
});
/**
 * CompensationResponse — one row. `effectiveTo` is null on the currently-
 * active compensation; non-null on historical rows.
 */
export const CompensationResponse = z.object({
    id: z.string(),
    employeeId: z.string(),
    effectiveFrom: IsoDateTime,
    effectiveTo: IsoDateTime.nullable(),
    grossSalary: MoneyAmount,
    currency: CurrencyCode,
    payFrequency: PayFrequency,
    createdAt: IsoDateTime,
    createdById: z.string(),
});
export const CompensationListResponse = z.object({
    compensations: z.array(CompensationResponse),
});
// ---------------------------------------------------------------------------
// Pay Components API — request/response schemas
// ---------------------------------------------------------------------------
/**
 * Operator-set pay component (allowance, bonus, etc).
 *
 * Editor accepts these kinds:
 *   - allowance         — recurring allowance (housing, transport, etc)
 *   - bonus             — one-time taxable addition (Q1 performance, signing)
 *   - thirteenth_month  — annual statutory / customary 13th-month payment
 *   - severance         — termination payment
 *   - final_settlement  — accrued vacation + severance combined at termination
 *
 * All of the above flow into the calculator as taxable gross additions
 * (the same code path as a `nature: 'other_taxable'` allowance). For
 * Wave 1 jurisdictions (NG / GH / CD) that's the correct flat-rate
 * treatment. Mid-year bonus tax smoothing is future work.
 *
 * `deduction` is deliberately NOT accepted here — it would reduce net
 * pay without reducing taxable gross, which is a fundamentally
 * different code path. Separate PR.
 *
 * Append-only: editing means ending the existing component (PATCH
 * /:id/end sets effectiveTo) and POSTing a fresh one with a later
 * effectiveFrom. Same shape as Compensation — pay history is forever.
 *
 * Nature semantics by kind:
 *   - allowance         → housing | transport | other_taxable | other_nontaxable
 *   - bonus / 13th /
 *     severance /
 *     final_settlement  → other_taxable (forced by the refine below;
 *                         these are always taxable gross additions)
 */
export const CreatePayComponentInput = z
    .object({
    employeeId: z.string().min(1),
    kind: z.enum([
        'allowance',
        'bonus',
        'thirteenth_month',
        'severance',
        'final_settlement',
    ]),
    nature: z.enum(['housing', 'transport', 'other_taxable', 'other_nontaxable']),
    label: z.string().min(1).max(100),
    amount: MoneyAmount,
    currency: CurrencyCode,
    effectiveFrom: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'effectiveFrom must be YYYY-MM-DD'),
    /**
     * Optional end date. Allowances are typically open-ended; one-time
     * bonus / severance / 13th-month / final-settlement components use
     * `effectiveTo == effectiveFrom` (the server doesn't auto-set this
     * — operators submit both dates equal for one-shot payments).
     */
    effectiveTo: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'effectiveTo must be YYYY-MM-DD')
        .optional(),
    /**
     * Free-text note captured in the audit log. Useful for documenting
     * the reason an allowance was added (HR letter ref, contract clause).
     */
    note: z.string().max(500).optional(),
})
    .refine((v) => !v.effectiveTo || v.effectiveTo >= v.effectiveFrom, { message: 'effectiveTo must be on or after effectiveFrom', path: ['effectiveTo'] })
    // One-time kinds (bonus / 13th / severance / final_settlement) must be
    // taxable. The interpreter treats `other_nontaxable` as excluded from
    // gross AND from the tax basis — that's correct for a non-taxable
    // reimbursement, wrong for a bonus. Force `other_taxable` (or one of
    // the other taxable natures) at the schema layer rather than letting
    // an operator misclassify a bonus as non-taxable and underpay tax.
    .refine((v) => v.kind === 'allowance' ||
    v.nature !== 'other_nontaxable', {
    message: 'Bonus / 13th-month / severance / final-settlement must be taxable. Pick a taxable nature.',
    path: ['nature'],
});
/** Query string for GET /v1/pay-components. */
export const ListPayComponentsQuery = z.object({
    employeeId: z.string().min(1),
    /**
     * If true, includes ended components. Default false — the typical
     * caller is the FE list view that just wants what's currently
     * effective. Audit / payroll snapshot use the DB directly.
     */
    includeEnded: z.coerce.boolean().optional().default(false),
});
/**
 * Request body for PATCH /v1/pay-components/:id/end. Records the
 * effective end date for an open allowance. Requires a `reason` —
 * "raise effective on..." vs "no longer applicable" matters for
 * the audit trail.
 */
export const EndPayComponentInput = z.object({
    effectiveTo: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'effectiveTo must be YYYY-MM-DD'),
    reason: z.string().min(3).max(500),
});
/**
 * PayComponentResponse — one row. `effectiveTo` is null on currently-
 * effective rows; non-null on ended ones.
 *
 * `taxable` is derived from `nature` for client-side display
 * convenience (badges, totals). Server is the source of truth.
 */
export const PayComponentResponse = z.object({
    id: z.string(),
    employeeId: z.string(),
    kind: PayComponentKind,
    nature: z.enum(['basic', 'housing', 'transport', 'other_taxable', 'other_nontaxable']),
    label: z.string(),
    amount: MoneyAmount,
    currency: CurrencyCode,
    effectiveFrom: IsoDateTime,
    effectiveTo: IsoDateTime.nullable(),
    createdAt: IsoDateTime,
});
export const PayComponentListResponse = z.object({
    components: z.array(PayComponentResponse),
});
// ---------------------------------------------------------------------------
// Employee Documents API — Tier 2 of the Mokoafrika HRIS-consolidation
// ---------------------------------------------------------------------------
/**
 * Operational HR document categories. Free-text label per document
 * inside each category — multiple "certificate" rows distinguish
 * themselves by label ("BSc transcript" vs "BSc certificate").
 */
export const EmployeeDocumentKind = z.enum([
    'certificate',
    'contract',
    'job_description',
    'id_document',
    'performance_review',
    'other',
]);
/**
 * HrArtifact enums + write contract (ADR-0011 D-X2, Phase 2). The Zod + inferred
 * type are sites 2 & 3 of the three-site enum discipline (Prisma is site 1).
 * MINIMAL SHELLS — `other` only; consuming module ADRs (0012–0016) extend them.
 * No `SUPPORTED_*` allowlist: nothing runtime-enforces these kinds yet (mirrors
 * SubscriptionTier's C-1 note). Keep these in lockstep with the Prisma enums.
 */
export const HrArtifactKind = z.enum(['other', 'candidate_cv', 'expense_receipt', 'asset_handover']);
export const HrArtifactOwnerType = z.enum(['other', 'candidate', 'expense_claim', 'asset_assignment']);
// Recruitment / Applicant Tracking (ADR-0012 Phase 1). Status machines, D-6.
// Three-site discipline (Prisma + Zod + @kora/types). Request-body schemas live
// inline in the route (apps/api/src/routes/recruitment.ts), mirroring the
// attendance/performance modules; only the enums are shared here.
export const RequisitionStatus = z.enum(['draft', 'pending_open_approval', 'open', 'on_hold', 'filled', 'closed']);
export const JobApplicationStatus = z.enum(['applied', 'screening', 'interview', 'offer', 'hired', 'rejected', 'withdrawn']);
// ADR-0012 Phase 2 — the offer lifecycle (D-6). draft → approved → extended
// (the extend-offer gate, D-7) → accepted / declined; an accepted offer is
// converted to an Employee (the convert seam, D-2).
export const JobOfferStatus = z.enum(['draft', 'approved', 'pending_extend_approval', 'extended', 'accepted', 'declined']);
// Expense Management (ADR-0020 Phase 1). Two-stage manager→finance approval,
// owned by the `ExpenseClaim` model. Three-site discipline (Prisma + Zod +
// inferred TS). Request-body schemas live inline in the route
// (apps/api/src/routes/expense.ts), mirroring attendance/recruitment; only the
// enums are shared here.
//
//   draft → pending_manager → pending_finance → approved
//         → (reimbursed | paid_offcycle)   [terminal — reached only at the
//            Phase-3 sweep / off-cycle; declared-not-yet-reachable in Phase 1]
//   plus rejected (from either stage) and cancelled (claimant, pre-approval).
export const ExpenseClaimStatus = z.enum([
    'draft',
    'pending_manager',
    'pending_finance',
    'approved',
    'reimbursed',
    'paid_offcycle',
    'rejected',
    'cancelled',
]);
// v1 expense categories (ADR-0020 D-6) — all genuine business costs (they map to
// a non-taxable `nature` at the Phase-3 sweep). A taxable perk category is
// deferred until a real use case.
export const ExpenseCategory = z.enum([
    'travel',
    'meals',
    'accommodation',
    'supplies',
    'communications',
    'other',
]);
/**
 * Asset Management enums (ADR-0024). Three-site discipline: these mirror the
 * Prisma enums (schema.prisma) and the request-body schemas live inline in
 * apps/api/src/routes/assets.ts (the Expense convention).
 *
 * AssetStatus is the STORED lifecycle — there is deliberately NO `assigned`
 * value (D5): available-vs-assigned is derived from an open assignment, so it
 * cannot drift. The API's DERIVED status (`DerivedAssetStatus`) adds `assigned`
 * at read time only.
 */
export const AssetStatus = z.enum(['available', 'retired', 'lost']);
/** The read-time status the API returns: the stored value, plus `assigned` when an open assignment exists (D5, derived — never stored). */
export const DerivedAssetStatus = z.enum(['available', 'assigned', 'retired', 'lost']);
export const AssetAssignmentStatus = z.enum([
    'requested',
    'approved',
    'issued',
    'returned',
    'rejected',
    'cancelled',
]);
/** ADR-0024 Phase 2b — how an assignment entered existence (provenance, D2). */
export const AssetAssignmentOrigin = z.enum(['state_machine', 'import']);
/**
 * Write contract for creating an HrArtifact (ADR-0011 polymorphic-owner
 * integrity, "validate ownerType/ownerId on write"). The first consuming module
 * route MUST validate its create payload through this — it enforces a known
 * `ownerType` (enum) + a non-empty `ownerId`. It does NOT (cannot yet) verify
 * the owner row exists — owner tables don't exist until a module ships; that
 * referential check is the consuming module's responsibility (see the model doc
 * + ADR-0011 orphan-cleanup note).
 */
export const HrArtifactCreateInput = z.object({
    ownerType: HrArtifactOwnerType,
    ownerId: z.string().trim().min(1, 'ownerId is required'),
    kind: HrArtifactKind,
    label: z.string().trim().max(255).optional(),
    filename: z.string().trim().min(1).max(255),
    contentType: z.string().trim().min(1).max(255),
    sizeBytes: z.number().int().nonnegative(),
    storageKey: z.string().trim().min(1),
});
/**
 * Attendance enums (ADR-0014 Phase 1). Sites 2 & 3 of the three-site discipline
 * (Prisma is site 1). `clock_in`/`clock_out` (not `in`/`out`). No `SUPPORTED_*`
 * allowlist — nothing runtime-gates these yet. Keep in lockstep with Prisma.
 */
export const ClockDirection = z.enum(['clock_in', 'clock_out']);
export const ClockSource = z.enum(['manual', 'self', 'kiosk', 'device']);
export const AttendanceOperatingMode = z.enum(['salaried_exception', 'hourly']);
/** Maximum upload size — 10 MB. Covers scanned PDFs of multi-page
 * contracts. Bigger files belong in dedicated DMS, not the HR app. */
export const EMPLOYEE_DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;
/** MIME-type allow-list. Matched at upload time; anything else is 415. */
export const EMPLOYEE_DOCUMENT_ALLOWED_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
];
/**
 * Metadata posted alongside the multipart file. The actual file is
 * the multipart `file` field; this is the JSON-stringified `metadata`
 * field. Two-field upload because Fastify's multipart plugin handles
 * one binary stream + accompanying form fields cleanly.
 */
export const UploadEmployeeDocumentMetadata = z.object({
    kind: EmployeeDocumentKind,
    label: z.string().min(1).max(120),
});
// ---------------------------------------------------------------------------
// Recruitment — candidate CV upload (ADR-0012 Phase 3, D-8)
// ---------------------------------------------------------------------------
/** Maximum candidate-CV upload size — 10 MB. A CV/résumé is a few pages; bigger
 *  files are almost always the wrong file. Mirrors the employee-document cap. */
export const CANDIDATE_CV_MAX_BYTES = 10 * 1024 * 1024;
/** MIME-type allow-list for candidate CVs (D-8 — a security control at the
 *  HrArtifact boundary). PDF + Word + common image scans only; executables,
 *  archives, and anything else are rejected with 415. The HrArtifact model
 *  makes content-type validation the consuming module's job — this is it. */
export const CANDIDATE_CV_ALLOWED_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
];
/** Optional operator-set label posted alongside the multipart CV file (JSON
 *  `metadata` field). The file itself is the multipart `file` field. */
export const UploadCandidateCvMetadata = z.object({
    label: z.string().min(1).max(120).optional(),
});
/** Maximum expense-receipt upload size — 10 MB (mirrors CANDIDATE_CV_MAX_BYTES).
 *  ADR-0020 Phase 2. */
export const EXPENSE_RECEIPT_MAX_BYTES = 10 * 1024 * 1024;
/** MIME-type allow-list for expense receipts (ADR-0020 Phase 2 — the security
 *  control at the HrArtifact boundary, mirroring CANDIDATE_CV_ALLOWED_TYPES).
 *  PDF + Word + common image scans only; anything else is rejected with 415. */
export const EXPENSE_RECEIPT_ALLOWED_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
];
/** ADR-0024 Phase 2a — asset handover forms / condition photos. Same 10 MB cap
 *  and allow-list as receipts (PDF + Word + image scans). */
export const ASSET_HANDOVER_MAX_BYTES = 10 * 1024 * 1024;
export const ASSET_HANDOVER_ALLOWED_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
/** Optional label posted alongside the multipart receipt file (JSON `metadata`
 *  field). The file itself is the multipart `file` field. */
export const UploadExpenseReceiptMetadata = z.object({
    label: z.string().min(1).max(120).optional(),
});
/**
 * EmployeeDocumentResponse — metadata only. Bytes flow through
 * GET /v1/employees/:id/documents/:docId/download (separate endpoint,
 * streams from storage with the right Content-Disposition).
 */
export const EmployeeDocumentResponse = z.object({
    id: z.string(),
    employeeId: z.string(),
    kind: EmployeeDocumentKind,
    label: z.string(),
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    uploadedById: z.string(),
    uploadedAt: IsoDateTime,
});
export const EmployeeDocumentListResponse = z.object({
    documents: z.array(EmployeeDocumentResponse),
});
// ---------------------------------------------------------------------------
// Payroll Runs API — request/response schemas
// ---------------------------------------------------------------------------
/** Status of a payroll run — mirrors the Prisma PayrollStatus enum. */
export const PayrollRunStatus = z.enum([
    'draft',
    'calculating',
    'calculated',
    'approved',
    'paid',
    'voided',
]);
const YyyyMmDd = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
/**
 * CreatePayrollRunInput — request body for POST /v1/payroll-runs.
 *
 * Period + payDate are YYYY-MM-DD. The server assembles the snapshot,
 * computes the idempotency key as `{orgId}:{periodStart}:{version}`, and
 * returns the newly-created run in `status: draft`. Calculation is kicked
 * off by a separate POST /v1/payroll-runs/:id/calculate call (per ADR-0005).
 */
export const CreatePayrollRunInput = z
    .object({
    periodStart: YyyyMmDd,
    periodEnd: YyyyMmDd,
    payDate: YyyyMmDd,
    /**
     * Optional explicit version. If omitted: defaults to 1 for a brand-new
     * period, or `supersedes.version + 1` when supersedesId is set.
     */
    version: z.number().int().positive().max(999).optional(),
    /**
     * ID of a previous PayrollRun being corrected by this one. When set:
     *   - the new run must be for the same periodStart as the superseded run
     *   - the server computes version = superseded.version + 1 unless the
     *     caller explicitly passes a higher `version`
     *   - when THIS run is later approved, the superseded run is auto-voided
     *     with reason "Superseded by v{N}" (skipped if it was already voided)
     *
     * Allowed superseded statuses: `approved` and `voided`. Both occupy
     * the (org, periodStart, version) slot, so both need a path forward
     * to a corrected v2. Approving v2 auto-voids v1 if it was approved;
     * if v1 was already manually voided, the auto-void is skipped.
     *
     * Rejected:
     *   - paid: payment-reversal is its own workflow.
     *   - draft / calculating / calculated: pre-terminal. Delete the
     *     draft or void the calculated run, then supersede the voided
     *     row.
     */
    supersedesId: z.string().optional(),
})
    .refine((v) => v.periodStart <= v.periodEnd, {
    message: 'periodStart must be <= periodEnd',
    path: ['periodStart'],
})
    .refine((v) => v.payDate >= v.periodEnd, {
    message: 'payDate must be on or after periodEnd',
    path: ['payDate'],
});
/**
 * ApprovePayrollRunInput — request body for POST /v1/payroll-runs/:id/approve.
 * `reason` is mandatory to create a persistent justification in the audit
 * trail (the set `REASON_REQUIRED_ACTIONS` in @kora/audit enforces this).
 */
export const ApprovePayrollRunInput = z.object({
    reason: z.string().min(1).max(500),
});
/**
 * VoidPayrollRunInput — request body for POST /v1/payroll-runs/:id/void.
 * Used both for explicit void and for the auto-void when a correction run
 * supersedes an approved run. A void is terminal; a voided run cannot be
 * revived — create a new run if needed.
 */
export const VoidPayrollRunInput = z.object({
    reason: z.string().min(1).max(500),
});
/** ListPayrollRunsQuery — query string for GET /v1/payroll-runs. */
export const ListPayrollRunsQuery = z.object({
    status: PayrollRunStatus.optional(),
    /** Opaque cursor — the previous page's last run id. */
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/**
 * GetPayrollRunQuery — query string for GET /v1/payroll-runs/:id.
 *
 * `includePayslips=true` inlines the full per-employee payslips in the
 * response. Off by default because payslips can be >1 MB for 1000-employee
 * orgs. The FE detail view fetches with `includePayslips=true` once it's on
 * the run detail page.
 */
export const GetPayrollRunQuery = z.object({
    includePayslips: z.coerce.boolean().default(false),
});
/**
 * PayslipBreakdownWire — the JSON payload stored in Payslip.breakdown and
 * echoed on the API boundary. Distinct from PayslipBreakdown (above) which
 * is a richer internal-only shape from an earlier design draft; this is
 * what the Day 12 route handler actually writes.
 *
 * Per-scheme contributions are the source of truth for displaying
 * "PenCom / NHF / ITF" (or country equivalent) on the FE, instead of
 * summing everything into a single "SS" line.
 */
export const PayslipContributionLine = z.object({
    scheme: z.string(),
    employeePeriod: MoneyAmount,
    employerPeriod: MoneyAmount,
});
export const PayslipBreakdownWire = z.object({
    ruleSpecHash: z.string(),
    employerSocialSecurity: MoneyAmount,
    contributions: z.array(PayslipContributionLine),
    fxConversion: z
        .object({
        fromCurrency: CurrencyCode,
        toCurrency: CurrencyCode,
        rate: z.string(),
    })
        .nullable(),
});
/**
 * PayslipResponse — one payslip on the API boundary. Money fields are
 * MoneyAmount strings with exactly 4 fractional digits.
 */
export const PayslipResponse = z.object({
    id: z.string(),
    employeeId: z.string(),
    employeeNumber: z.string(),
    /**
     * Denormalised employee identity, copied from the Employee row at the
     * moment the payroll run was retrieved. Lets the FE render readable
     * names ("Pierre Lumumba (EMP-001)") on payroll detail tables, payslip
     * PDFs, and exports without a second round-trip per row.
     *
     * These reflect the CURRENT state of the Employee, not a snapshot.
     * For an audit-grade "who was paid this exact name on date X" record,
     * see calculationInputsSnapshot on the PayrollRun.
     */
    employeeFirstName: z.string(),
    employeeMiddleName: z.string().nullable(),
    employeeLastName: z.string(),
    employeeCountry: CountryCode,
    currency: CurrencyCode,
    grossSalary: MoneyAmount,
    taxAmount: MoneyAmount,
    socialSecurity: MoneyAmount,
    otherDeductions: MoneyAmount,
    netSalary: MoneyAmount,
    ruleSpecHash: z.string(),
    fxConversion: z
        .object({
        fromCurrency: CurrencyCode,
        toCurrency: CurrencyCode,
        rate: z.string(),
    })
        .nullable(),
    /**
     * Per-scheme contribution breakdown. Untyped on the wire as `unknown` here
     * because the server writes this as JSONB from the orchestrator; the FE
     * parses it with PayslipBreakdownWire before rendering. Using `unknown`
     * forces callers to validate before trusting the shape.
     */
    breakdown: z.unknown().optional(),
    createdAt: IsoDateTime,
});
/**
 * PayrollRunResponse — public shape for a single run. `calculationInputsSnapshot`
 * is deliberately NOT exposed here — it can be 500 KB+ and has PII. A
 * dedicated admin endpoint will serve it in a later sprint for auditors.
 */
export const PayrollRunResponse = z.object({
    id: z.string(),
    organizationId: z.string(),
    status: PayrollRunStatus,
    periodStart: IsoDateTime,
    periodEnd: IsoDateTime,
    payDate: IsoDateTime,
    version: z.number().int().positive(),
    supersedesId: z.string().nullable(),
    idempotencyKey: z.string(),
    ruleSpecHash: z.string(),
    interpreterVersion: z.string(),
    totals: z
        .object({
        currency: CurrencyCode,
        grossTotal: MoneyAmount,
        taxTotal: MoneyAmount,
        employeeSocialSecurityTotal: MoneyAmount,
        employerSocialSecurityTotal: MoneyAmount,
        netTotal: MoneyAmount,
        employeeCount: z.number().int().nonnegative(),
    })
        .nullable(),
    createdById: z.string(),
    approvedById: z.string().nullable(),
    approvedAt: IsoDateTime.nullable(),
    approvalReason: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    /** Inlined payslips when includePayslips=true on GET /:id. */
    payslips: z.array(PayslipResponse).optional(),
});
/** PayrollRunListResponse — keyset-paginated list. */
export const PayrollRunListResponse = z.object({
    runs: z.array(PayrollRunResponse),
    nextCursor: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Employee self-service portal — Sprint 2 employee role
// ---------------------------------------------------------------------------
/**
 * ProvisionableUserRole — the roles a provision/role-change flow may assign.
 * `owner` is deliberately excluded: ownership transfer is a separate workflow.
 * Used by both ProvisionEmployeeUserInput and ChangeUserRoleInput so the
 * allow-list lives in exactly one place.
 */
export const ProvisionableUserRole = z.enum(['hr', 'finance', 'admin', 'employee', 'it']);
/**
 * ProvisionEmployeeUserInput — request body for an owner/admin/HR creating a
 * login account for an existing employee. The provisioned user is created in
 * `invited` status and receives a token-bearing email link; they set their own
 * password on the acceptance page (mirrors the password-reset flow). No
 * password is set or transmitted by this request.
 *
 * Constraints:
 *   - role: one of hr | finance | admin | employee (not owner).
 *   - email: the LOGIN email, unique within the org. Optional — if omitted, the
 *     server falls back to the Employee's recorded `email`. The Employee's
 *     `email` field is a separate fact (where HR mailed the contract); the
 *     User's email is for login. They live independently.
 */
export const ProvisionEmployeeUserInput = z.object({
    email: z.string().email().max(255).optional(),
    role: ProvisionableUserRole,
});
/**
 * InvitationAcceptBody — `POST /v1/auth/invitation/accept`.
 *
 * Unauthenticated. The token is the raw value from the emailed invitation URL;
 * the server hashes it and looks up the corresponding row. Password rules are
 * enforced by validatePasswordStrength in the handler — Zod just bounds the
 * shape, matching PasswordResetConfirmBody.
 */
export const InvitationAcceptBody = z
    .object({
    token: z.string().min(1).max(512),
    password: z.string().min(1).max(512),
})
    .strict();
/**
 * ChangeUserRoleInput — `PATCH /v1/employees/:id/user/role`.
 *
 * Changes a provisioned user's role. Target role excludes `owner` (ownership
 * transfer is a separate workflow). Optional free-text reason is recorded on
 * the audit event and surfaced in the notification email.
 */
export const ChangeUserRoleInput = z
    .object({
    role: ProvisionableUserRole,
    reason: z.string().max(500).optional(),
})
    .strict();
/**
 * MeProfileResponse — `GET /v1/me/profile` body. The current
 * employee's User row + their Employee record. Used by the FE to
 * render the /me dashboard, gate menu items, and seed the
 * profile-edit form.
 *
 * Composes existing `UserResponse` + `EmployeeResponse` so we get
 * field-level validation for free and changes there propagate here.
 */
export const MeProfileResponse = z.object({
    user: UserResponse,
    employee: EmployeeResponse,
});
/**
 * MePayslipResponse — one row in the employee's payslip list. Strict
 * subset of `PayslipResponse` (admin-side) — the employee doesn't
 * need rule-spec hashes or breakdowns at the list level. Period +
 * status + headline numbers, plus the run's status so the FE can
 * render "voided" pills clearly when an old payslip was superseded.
 */
/**
 * ADR-0019 — the pay-currency view of a payslip (the currency the employee is
 * actually paid in, when it differs from the country's statutory currency).
 */
export const PayslipPayCurrency = z.object({
    currency: CurrencyCode,
    gross: MoneyAmount,
    /** Take-home in the pay currency (net of statutory + advances). */
    takeHome: MoneyAmount,
    netBeforeAdvances: MoneyAmount,
    advanceDeductions: MoneyAmount,
});
export const MePayslipResponse = z.object({
    id: z.string(),
    payrollRunId: z.string(),
    /** YYYY-MM-DD calendar dates (no timezone games). */
    periodStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    periodEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    payDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /**
     * Run status. Only `approved`, `paid`, and `voided` are surfaced
     * to employees — drafts/calculated/calculating are not yet finalised
     * and shouldn't be visible. We include `voided` so an employee
     * sees the historical record + the corrected v2 alongside it.
     */
    runStatus: PayrollRunStatus,
    version: z.number().int().positive(),
    currency: CurrencyCode,
    grossSalary: MoneyAmount,
    netSalary: MoneyAmount,
    /**
     * ADR-0019 pay-currency view. Present when the employee is paid in a currency
     * other than their country's statutory currency (e.g. USD on a CDF/DRC run):
     * `currency`/`grossSalary`/`netSalary` above stay the statutory (spec) figures
     * — what the regulator filing uses — while this is what the employee is
     * actually paid. Absent for same-currency employees.
     */
    payCurrency: PayslipPayCurrency.nullable().optional(),
});
/** Wrapper response for `GET /v1/me/payslips`. */
export const MePayslipsListResponse = z.object({
    payslips: z.array(MePayslipResponse),
});
/**
 * MyDocumentResponse — one row in `GET /v1/me/documents`.
 *
 * Strict subset of `EmployeeDocumentResponse` (the admin shape) —
 * `uploadedById` is stripped because the employee doesn't need to
 * know which HR user uploaded the file, and `employeeId` is implied
 * (every row in this response belongs to the caller).
 */
export const MyDocumentResponse = z.object({
    id: z.string(),
    kind: EmployeeDocumentKind,
    label: z.string(),
    filename: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    uploadedAt: IsoDateTime,
});
export const MyDocumentsListResponse = z.object({
    documents: z.array(MyDocumentResponse),
});
// ---------------------------------------------------------------------------
// Leave management — substrate (PR 4a, ADR 0008)
// ---------------------------------------------------------------------------
/**
 * Leave taxonomy enum. Mirrors the Prisma `LeaveType` enum exactly so
 * FE + API can use the same string literals without importing the
 * generated Prisma client. See ADR 0008 D6.
 */
export const LeaveType = z.enum([
    'annual',
    'sick',
    'maternity',
    'paternity',
    'compassionate',
    'unpaid',
    // ADR-0022 C1 / D1 — non-statutory policy-default types (mirror Prisma order).
    'casual',
    'study',
    'other',
]);
/**
 * Reserved leave-type keys (ADR-0023, guardrail #2). Derived PROGRAMMATICALLY
 * from the built-in `LeaveType` enum — not a hand-maintained parallel list — so
 * a future statutory type added to the enum auto-reserves. A custom
 * `LeaveTypeDefinition.key` may not collide with a built-in. Normalized
 * (lower-cased + trimmed) for case/whitespace-insensitive comparison. Defined
 * now (Phase 1) though the create path that checks it is Phase 2.
 */
export const RESERVED_LEAVE_TYPE_KEYS = new Set(LeaveType.options.map((k) => k.toLowerCase().trim()));
/** True if `key` collides with a built-in leave type (case/space-insensitive). */
export function isReservedLeaveTypeKey(key) {
    return RESERVED_LEAVE_TYPE_KEYS.has(key.toLowerCase().trim());
}
/**
 * Ledger entry discriminator (ADR 0008 D1). Mirrors the Prisma
 * `LeaveTransactionKind` enum.
 */
export const LeaveTransactionKind = z.enum([
    'grant',
    'debit',
    'refund',
    'correction',
    'forfeit',
    'expiry',
    'termination_payout',
]);
/** Five-state workflow (ADR 0008 D16). Mirrors Prisma `LeaveRequestStatus`. */
export const LeaveRequestStatus = z.enum([
    'draft',
    'submitted',
    'approved',
    'rejected',
    'cancelled',
]);
/** Half-day boundary discriminator (ADR 0008 D10). Mirrors Prisma `DayPart`. */
export const DayPart = z.enum(['full', 'morning_half', 'afternoon_half']);
/** Carryover strategy enum mirrored from the country spec shape. */
export const LeaveCarryoverStrategy = z.enum(['cap', 'all', 'expiry', 'forfeit']);
/**
 * Accrual model. Mirrors the YAML enum in @kora/compliance (kept in
 * sync by hand — the two packages don't share imports). See
 * `packages/compliance/src/leave-types.ts` for the canonical
 * definition and per-value semantics.
 */
export const LeaveAccrualStrategy = z.enum(['monthly', 'lump_annual']);
/**
 * EffectiveLeavePolicy — what `getEffectiveLeavePolicy` returns and
 * what the FE renders in the admin policy editor. Country baseline
 * merged with any org override row; provenance captured in `source`.
 *
 * Some fields can be null in the merged result (e.g. `annualDays`
 * for `unpaid` leave where the country spec defines no cap). The FE
 * renders `null` as "—" or "no cap".
 */
export const EffectiveLeavePolicy = z.object({
    leaveType: LeaveType,
    countryCode: CountryCode,
    enabled: z.boolean(),
    annualDays: z.number().nonnegative().nullable(),
    maxCarryoverDays: z.number().nonnegative().nullable(),
    carryoverStrategy: LeaveCarryoverStrategy,
    carryoverExpiresOn: z.string().nullable(), // "MM-DD" or null
    affectsPay: z.boolean(),
    accrualStrategy: LeaveAccrualStrategy,
    /** Resolution provenance — for debugging + the admin UI's "default vs
     *  override" badge. */
    source: z.enum(['country', 'org_override']),
});
/**
 * Country-default snapshot for the admin UI. Shown read-only beside
 * each editable override field.
 */
export const CountryLeaveDefault = z.object({
    leaveType: LeaveType,
    countryCode: CountryCode,
    enabled: z.boolean(),
    annualDays: z.number().nonnegative().nullable(),
    maxCarryoverDays: z.number().nonnegative().nullable(),
    carryoverStrategy: LeaveCarryoverStrategy,
    affectsPay: z.boolean(),
    accrualStrategy: LeaveAccrualStrategy,
    // ADR-0022 C2 — country-default minimum advance notice (calendar days).
    minNoticeDays: z.number().int().nonnegative(),
    // ADR-0022 C1 — whether annualDays is a statutory floor (overrides may only
    // exceed it) or a product default (any value ≥ 0 allowed, incl. 0).
    hasStatutoryFloor: z.boolean(),
});
/**
 * Per-org override snapshot returned alongside the country default.
 * `annualDaysOverride: null` means "use country default for this
 * field" — there may still be an override row with other non-null
 * fields. `null` for the whole row (no row at all) is represented
 * by omitting it from the response.
 */
export const OrgLeaveOverride = z.object({
    id: z.string(),
    annualDaysOverride: z.number().nonnegative().nullable(),
    maxCarryoverDaysOverride: z.number().nonnegative().nullable(),
    // ADR-0022 C2 — per-org minimum-notice override (calendar days), or null.
    minNoticeDaysOverride: z.number().int().nonnegative().nullable(),
    effectiveFrom: IsoDateTime,
    effectiveTo: IsoDateTime.nullable(),
});
/**
 * One row in the admin policy editor: the country baseline plus
 * (optionally) the live org override that supersedes it.
 */
export const LeavePolicyRow = z.object({
    countryCode: CountryCode,
    leaveType: LeaveType,
    countryDefault: CountryLeaveDefault,
    override: OrgLeaveOverride.nullable(),
});
/**
 * Org-wide leave settings (not per-type). ADR-0022 C3 — whether public
 * holidays inside a leave span count as leave days (default false).
 */
export const OrgLeaveSettings = z.object({
    countPublicHolidaysInLeave: z.boolean(),
});
/**
 * ADR-0023 Phase 2 — an org-defined custom leave type, as returned to the
 * policy editor. `affectsPay` is NOT surfaced: it is structurally false in v1
 * (guardrail #4). `hasHistory` tells the UI whether delete is allowed (false)
 * or only retire (true) — a definition with ledger history can't be deleted.
 */
export const LeaveTypeDefinitionRow = z.object({
    id: z.string(),
    key: z.string(),
    label: z.string(),
    accrualStrategy: LeaveAccrualStrategy,
    carryoverStrategy: LeaveCarryoverStrategy,
    annualDays: z.number().nullable(),
    active: z.boolean(),
    hasHistory: z.boolean(),
});
/**
 * Create a custom leave type. THE load-bearing validation boundary (ADR-0023
 * Phase 2): Phase 1's resolver casts accrual/carryover strategy with no runtime
 * check, trusting THIS schema. `key` is normalized (trim + lower) then must be a
 * slug, must NOT collide with a built-in (guardrail #2), and is unique-per-org
 * (enforced at the DB, surfaced as 409). `affectsPay` is intentionally absent.
 */
export const CreateLeaveTypeDefinitionInput = z
    .object({
    key: z.preprocess((v) => (typeof v === 'string' ? v.trim().toLowerCase() : v), z
        .string()
        .min(1, 'key is required')
        .max(40, 'key too long')
        .regex(/^[a-z0-9_]+$/, 'key must be lowercase letters, digits, or underscore')
        .refine((k) => !isReservedLeaveTypeKey(k), 'key collides with a built-in leave type')),
    label: z.string().trim().min(1, 'label is required').max(80, 'label too long'),
    accrualStrategy: LeaveAccrualStrategy,
    carryoverStrategy: LeaveCarryoverStrategy,
    annualDays: z.number().nonnegative('annualDays must be ≥ 0').nullable(),
})
    .strict();
/**
 * Update a custom leave type — `label` (rename) and/or `active` (retire) ONLY.
 * `.strict()` makes any attempt to change `key` (or set `affectsPay`, strategy,
 * etc.) a 400 — key is immutable because the ledger keys on it (D5).
 */
export const UpdateLeaveTypeDefinitionInput = z
    .object({
    label: z.string().trim().min(1, 'label is required').max(80, 'label too long').optional(),
    active: z.boolean().optional(),
})
    .strict()
    .refine((b) => b.label !== undefined || b.active !== undefined, 'nothing to update');
export const LeavePoliciesListResponse = z.object({
    policies: z.array(LeavePolicyRow),
    settings: OrgLeaveSettings,
    /** ADR-0023 Phase 2 — the org's custom leave types (active + retired). */
    definitions: z.array(LeaveTypeDefinitionRow),
});
/** Body for PATCH /v1/leave-policies/settings (ADR-0022 C3). */
export const UpdateLeaveSettingsInput = z
    .object({
    countPublicHolidaysInLeave: z.boolean(),
})
    .strict();
/**
 * Body for PUT `/v1/leave-policies/:countryCode/:leaveType`. Both
 * fields are optional — null means "clear this override" (fall back
 * to country default). Omitting a field means "leave it unchanged on
 * the current row".
 *
 * .strict() blocks attempts to smuggle fields (leaveType, country,
 * effectiveFrom, ...) that the route resolves from URL params or
 * computes itself.
 */
export const UpdateLeavePolicyInput = z
    .object({
    annualDaysOverride: z.number().nonnegative().max(366).nullable().optional(),
    maxCarryoverDaysOverride: z
        .number()
        .nonnegative()
        .max(366)
        .nullable()
        .optional(),
    // Minimum advance notice in calendar days (ADR-0022 D2). Whole days;
    // no floor (0 is the natural minimum). Rejected as >0 for statutory
    // medical/emergency types at the route (see leave-policies).
    minNoticeDaysOverride: z
        .number()
        .int()
        .nonnegative()
        .max(365)
        .nullable()
        .optional(),
})
    .strict();
/**
 * UpdateMyProfileInput — request body for `PATCH /v1/me/profile`.
 *
 * The editable surface is deliberately narrow: contact details + emergency
 * contact only. Identity (name, gender, DOB), employment (department,
 * position, employmentType, workLocation), statutory IDs (TIN,
 * PenCom/SSNIT/CNSS), and banking are HR-managed — an employee patching
 * those would let them spoof tax filings.
 *
 * `.strict()` is the security boundary: any unknown key is rejected with
 * 400 BEFORE the route handler runs. Without strict, an attacker could
 * smuggle `salary` or `socialSecurityId` into the body and bypass the
 * intended allow-list.
 *
 * Field semantics:
 *   - All four fields are optional. Sending the empty body `{}` is a
 *     no-op (handler returns the current profile without writing an
 *     audit event).
 *   - `null` for `phone`/`personalEmail`/`homeAddress` clears the value.
 *   - `null` for `nextOfKin` clears the entire emergency-contact block.
 *     Sending a partial `nextOfKin` object re-validates against the
 *     existing `NextOfKin` schema (re-used, not redefined).
 */
export const UpdateMyProfileInput = z
    .object({
    phone: z.string().min(4).max(30).nullable().optional(),
    personalEmail: z.string().email().max(255).nullable().optional(),
    homeAddress: z.string().min(1).max(500).nullable().optional(),
    nextOfKin: NextOfKin.nullable().optional(),
})
    .strict();
/**
 * ChangePasswordInput — request body for `POST /v1/me/password/change`.
 *
 * Both fields are required strings. The plaintext length / character-class
 * rules live in `validatePasswordStrength` (packages/types/src/password.ts)
 * — the route handler runs that *after* Zod parsing so the response
 * surfaces a `weak_password` error with a structured `reason`, rather than
 * a generic Zod validation issue. The schema's only job is to enforce
 * shape + reject unknown keys (.strict() — defense against a field-
 * smuggling attempt). Wide bounds (1..512) keep the schema from rejecting
 * extreme-length passwords before the dedicated validator gets a look.
 */
export const ChangePasswordInput = z
    .object({
    currentPassword: z.string().min(1).max(512),
    newPassword: z.string().min(1).max(512),
})
    .strict();
/**
 * PasswordResetRequestBody — `POST /v1/auth/password/reset/request`.
 *
 * Unauthenticated. The server returns 200 with a canonical message
 * regardless of whether the (orgSlug, email) combination resolves to
 * a real account — no enumeration via this endpoint. See the route
 * handler in auth.ts for the constant-time treatment of the four
 * branches (org missing / user missing / send fails / success).
 */
export const PasswordResetRequestBody = z
    .object({
    orgSlug: OrgSlug,
    email: z.string().email().max(255),
})
    .strict();
/**
 * PasswordResetConfirmBody — `POST /v1/auth/password/reset/confirm`.
 *
 * Unauthenticated. The token is the raw value from the emailed reset
 * URL; the server hashes it and looks up the corresponding row.
 *
 * Like ChangePasswordInput, the password rules are enforced by
 * validatePasswordStrength in the handler — Zod just bounds the
 * shape. Wide string bounds (1..512) match.
 */
export const PasswordResetConfirmBody = z
    .object({
    token: z.string().min(1).max(512),
    newPassword: z.string().min(1).max(512),
})
    .strict();
/**
 * SessionResponse — one row in `GET /v1/me/sessions`.
 *
 * `identifier` is the last 4 hex characters of the token's SHA-256
 * hash — a stable user-facing handle (e.g. "Revoke a3f2") that leaks
 * no real entropy (16 bits / 240 bits preimage resistance remains).
 *
 * `isCurrent` is true iff this row's tokenHash matches the request's
 * refresh cookie hash. The FE disables the per-session revoke button
 * for the current session (the user goes through /v1/auth/logout
 * instead).
 */
export const SessionResponse = z.object({
    id: z.string(),
    identifier: z.string().length(4),
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    issuedAt: IsoDateTime,
    lastUsedAt: IsoDateTime.nullable(),
    isCurrent: z.boolean(),
});
export const SessionListResponse = z.object({
    sessions: z.array(SessionResponse),
});
/** Response body for `DELETE /v1/me/sessions` (all-other). */
export const RevokeAllSessionsResponse = z.object({
    revokedCount: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Leave management — user-facing flow (PR 4b, ADR 0008)
// ---------------------------------------------------------------------------
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD');
/**
 * SubmitLeaveRequestInput — POST /v1/leave/requests body. The
 * employee creating their own request — server resolves who the
 * employee is from `req.userId → User.employeeId`. .strict() blocks
 * smuggling `employeeId` or `status`.
 */
/**
 * ADR-0023 Phase 3b — a leave-type KEY on the wire: a built-in value OR an
 * org-defined custom key. Format-validated only (lowercase slug); WHETHER the
 * key is real/active/submittable is decided authoritatively by the resolver's
 * `enabled` gate at submit time (not by an enum here). Built-in values all match
 * this shape, so existing submissions are unaffected.
 */
export const LeaveTypeKey = z.string().trim().min(1).max(40).regex(/^[a-z0-9_]+$/);
export const SubmitLeaveRequestInput = z
    .object({
    leaveType: LeaveTypeKey,
    startDate: IsoDate,
    endDate: IsoDate,
    startDayPart: DayPart.optional().default('full'),
    endDayPart: DayPart.optional().default('full'),
    reason: z.string().max(500).optional(),
})
    .strict();
/** Backdated path — admin/owner only, accepts past dates, also takes
 *  the employee id explicitly + requires a non-empty reason
 *  (REASON_REQUIRED_ACTIONS). */
export const BackdatedLeaveRequestInput = z
    .object({
    employeeId: z.string().min(1),
    leaveType: LeaveType,
    startDate: IsoDate,
    endDate: IsoDate,
    startDayPart: DayPart.optional().default('full'),
    endDayPart: DayPart.optional().default('full'),
    reason: z.string().min(5).max(500),
})
    .strict();
export const ApproveLeaveRequestInput = z
    .object({
    /** Optional comment shown to the employee. */
    note: z.string().max(500).optional(),
})
    .strict();
export const RejectLeaveRequestInput = z
    .object({
    reason: z.string().min(5).max(500),
})
    .strict();
export const CancelLeaveRequestInput = z
    .object({
    reason: z.string().max(500).optional(),
})
    .strict();
/** Balance-correction body (HR-only). */
export const BalanceCorrectionInput = z
    .object({
    employeeId: z.string().min(1),
    leaveType: LeaveType,
    dayDelta: z.number().refine((v) => v !== 0, { message: 'dayDelta must be non-zero' }),
    reason: z.string().min(10).max(500),
})
    .strict();
/**
 * GET /v1/leave/requests query params.
 *
 * `view`:
 *   - `mine` — the caller's own requests (default for employees).
 *   - `pending_my_approval` — requests where approverId === caller.
 *   - `all` — every request in the org. HR/admin/owner only; the
 *     route rejects other roles asking for this view.
 */
export const ListLeaveRequestsQuery = z.object({
    view: z.enum(['mine', 'pending_my_approval', 'all']).optional().default('mine'),
    status: LeaveRequestStatus.optional(),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
});
/**
 * LeaveRequestResponse — the row shape returned by /v1/leave/requests.
 * Denormalises the employee's name + employee number for FE
 * convenience (the manager queue needs to render "Adaeze Okafor"
 * without a separate lookup per row).
 */
export const LeaveRequestResponse = z.object({
    id: z.string(),
    employeeId: z.string(),
    employeeNumber: z.string(),
    employeeFirstName: z.string(),
    employeeLastName: z.string(),
    leaveType: LeaveType,
    startDate: IsoDate,
    endDate: IsoDate,
    startDayPart: DayPart,
    endDayPart: DayPart,
    workingDaysCount: z.number().nonnegative(),
    status: LeaveRequestStatus,
    approverId: z.string().nullable(),
    approverName: z.string().nullable(),
    reason: z.string().nullable(),
    decidedAt: IsoDateTime.nullable(),
    decidedById: z.string().nullable(),
    decisionReason: z.string().nullable(),
    cancelledAt: IsoDateTime.nullable(),
    cancelledById: z.string().nullable(),
    isBackdated: z.boolean(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const LeaveRequestListResponse = z.object({
    requests: z.array(LeaveRequestResponse),
});
/**
 * /v1/me/leave/balance row. Available = accrued - used. Pending is
 * informational only — the FE shows it but the resolver doesn't
 * deduct from available (insufficient-balance check happens at submit
 * time against actual ledger sum).
 */
export const LeaveBalanceRow = z.object({
    // ADR-0023 Phase 3a — a built-in value OR an org-defined custom type key, so
    // this is a string (not the LeaveType enum). Consumers must label it via a
    // lookup-with-fallback, never an exhaustive Record<LeaveType>.
    leaveType: z.string(),
    available: z.number(),
    accrued: z.number().nonnegative(),
    used: z.number().nonnegative(),
    pending: z.number().nonnegative(),
});
export const LeaveBalanceResponse = z.object({
    balances: z.array(LeaveBalanceRow),
});
/** One row in the employee's ledger history. */
export const LeaveTransactionResponse = z.object({
    id: z.string(),
    leaveType: LeaveType,
    dayDelta: z.number(),
    kind: LeaveTransactionKind,
    effectiveDate: IsoDateTime,
    recordedAt: IsoDateTime,
    reason: z.string().nullable(),
    leaveRequestId: z.string().nullable(),
});
export const LeaveTransactionListResponse = z.object({
    transactions: z.array(LeaveTransactionResponse),
});
/** Working-days preview returned by the FE form before submit.
 *  Pure-compute response from `POST /v1/leave/requests/preview-days`
 *  so the FE doesn't reimplement the working-days math. */
export const LeaveDaysPreviewInput = z
    .object({
    startDate: IsoDate,
    endDate: IsoDate,
    startDayPart: DayPart.optional().default('full'),
    endDayPart: DayPart.optional().default('full'),
})
    .strict();
export const LeaveDaysPreviewResponse = z.object({
    workingDaysCount: z.number().nonnegative(),
    workingDates: z.array(z.object({
        date: IsoDate,
        portion: z.number(),
    })),
});
// ---------------------------------------------------------------------------
// Compliance reports (PR ⑥a / ⑥b) — per-period regulator filing artefacts
// ---------------------------------------------------------------------------
//
// Promoted from inline BE/FE declarations to shared types (PR ⑥b). The
// download endpoint streams csvContent separately, so it's intentionally
// absent from the metadata response below.
export const RegulatorFormat = z.enum(['combined_drc', 'combined_ng', 'combined_gh']);
export const ComplianceReportResponse = z.object({
    id: z.string(),
    payrollRunId: z.string(),
    countryCode: z.string(),
    regulatorFormat: RegulatorFormat,
    periodYear: z.number().int(),
    periodMonth: z.number().int(),
    generatedAt: IsoDateTime,
    generatedById: z.string(),
    csvSha256: z.string(),
    rowCount: z.number().int(),
    columnCount: z.number().int(),
    createdAt: IsoDateTime,
});
export const ComplianceReportListResponse = z.object({
    reports: z.array(ComplianceReportResponse),
});
export const ListComplianceReportsQuery = z.object({
    periodYear: z.number().int().optional(),
    periodMonth: z.number().int().optional(),
    countryCode: z.string().optional(),
    payrollRunId: z.string().optional(),
});
//# sourceMappingURL=domain.js.map